'use client';

import * as React from 'react';
import { useRoomContext } from '@livekit/components-react';
import { RoomEvent, Track, RemoteParticipant, LocalParticipant } from 'livekit-client';
import {
  MicIcon,
  MicDisabledIcon,
  CameraIcon,
  CameraDisabledIcon,
} from '@livekit/components-react';
import { useSearchParams } from 'next/navigation';
import {
  isRestrictedRole,
  parseHandRaisedAt,
  parseParticipantOverride,
  parseParticipantRole,
  parseRoomPolicy,
  resolveMediaLock,
  type RoomMediaPolicy,
} from './mediaPolicy';
import { parseLobby, pendingLobbyEntries, type LobbyEntry } from './lobby';
import { ShareLinkButton } from './ShareLinkButton';

export interface ParticipantsDrawerProps extends React.HTMLAttributes<HTMLDivElement> {
  canMute?: boolean;
  onClose?: () => void;
}

export function ParticipantsDrawer({ canMute = false, onClose, ...props }: ParticipantsDrawerProps) {
  const room = useRoomContext();
  const searchParams = useSearchParams();
  const [participants, setParticipants] = React.useState<(LocalParticipant | RemoteParticipant)[]>([]);
  const [muteStatuses, setMuteStatuses] = React.useState<Map<string, boolean>>(new Map());
  const [mutingParticipants, setMutingParticipants] = React.useState<Set<string>>(new Set());
  const [cameraStatuses, setCameraStatuses] = React.useState<Map<string, boolean>>(new Map());
  const [togglingCameras, setTogglingCameras] = React.useState<Set<string>>(new Set());
  // boolean | undefined — undefined = no individual override, follow room policy.
  const [micEnabledStatuses, setMicEnabledStatuses] = React.useState<Map<string, boolean | undefined>>(new Map());
  const [cameraEnabledStatuses, setCameraEnabledStatuses] = React.useState<Map<string, boolean | undefined>>(new Map());
  const [screenShareEnabledStatuses, setScreenShareEnabledStatuses] = React.useState<Map<string, boolean | undefined>>(new Map());
  // Read-only members and admitted guests. Everything is off for them until
  // it's granted, so the toggles below have to know who they are.
  const [restrictedSids, setRestrictedSids] = React.useState<Set<string>>(new Set());
  const [togglingScreenShareEnabled, setTogglingScreenShareEnabled] = React.useState<Set<string>>(new Set());
  const [roomPolicy, setRoomPolicy] = React.useState<RoomMediaPolicy>({});
  // People knocking at the door. Read off room metadata, which LiveKit pushes
  // to us on change AND ships in the JoinResponse — so a moderator who joins
  // after someone knocked still finds them here, with nothing polled.
  const [lobbyEntries, setLobbyEntries] = React.useState<LobbyEntry[]>([]);
  const [decidingTickets, setDecidingTickets] = React.useState<Set<string>>(new Set());
  // sid -> ms timestamp of when they raised. Timestamp not boolean, so the
  // queue can be worked in the order people actually asked.
  const [handRaisedAt, setHandRaisedAt] = React.useState<Map<string, number>>(new Map());
  const [loweringHands, setLoweringHands] = React.useState<Set<string>>(new Set());
  const [applyingRoomPolicy, setApplyingRoomPolicy] = React.useState<Set<string>>(new Set());
  const [togglingMicEnabled, setTogglingMicEnabled] = React.useState<Set<string>>(new Set());
  const [togglingCameraEnabled, setTogglingCameraEnabled] = React.useState<Set<string>>(new Set());
  const participantListenersRef = React.useRef<Map<string, Array<() => void>>>(new Map());
  
  // Invite modal state
  const [isInviteModalOpen, setIsInviteModalOpen] = React.useState(false);
  const [inviteMethod, setInviteMethod] = React.useState<'email' | 'phone'>('email');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [nickname, setNickname] = React.useState('');
  const [errors, setErrors] = React.useState<{ email?: string; phone?: string; nickname?: string; _submit?: string }>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Get mute status for a participant
  const getMuteStatus = React.useCallback((participant: LocalParticipant | RemoteParticipant): boolean => {
    const micTrack = participant.getTrackPublication(Track.Source.Microphone);
    console.log('particioant', participant.identity, micTrack?.isMuted, micTrack?.isSubscribed);
    
    if (!micTrack) {
      return true; // No mic track means effectively muted
    }
    
    // The isMuted property should be available on the publication
    // even if the track isn't subscribed yet for remote participants
    return micTrack.isMuted ?? true;
  }, []);

  // Get camera status for a participant
  const getCameraStatus = React.useCallback((participant: LocalParticipant | RemoteParticipant): boolean => {
    const cameraTrack = participant.getTrackPublication(Track.Source.Camera);
    console.log('participant camera', participant.identity, cameraTrack?.isMuted, cameraTrack?.isSubscribed);
    
    if (!cameraTrack) {
      return true; // No camera track means effectively muted
    }
    
    // The isMuted property should be available on the publication
    // even if the track isn't subscribed yet for remote participants
    return cameraTrack.isMuted ?? true;
  }, []);

  // Read the individual override from participant metadata.
  //
  // TRI-STATE — `undefined` means "no override, follow the room policy" and is
  // NOT the same as `true`. The old `?? true` here collapsed the two, which is
  // fine with no room-wide mute but wrong once there is one: it would report
  // everyone as enabled while the room policy had them muted.
  const readEnabledStateFromMetadata = React.useCallback((participant: LocalParticipant | RemoteParticipant) => {
    return parseParticipantOverride(participant.metadata);
  }, []);

  // Update participants list
  const updateParticipants = React.useCallback(() => {
    const allParticipants: (LocalParticipant | RemoteParticipant)[] = [room.localParticipant];
    room.remoteParticipants.forEach((participant) => {
      allParticipants.push(participant);
    });
    setParticipants(allParticipants);

    // Update mute statuses
    const newMuteStatuses = new Map<string, boolean>();
    const newCameraStatuses = new Map<string, boolean>();
    const newMicEnabledStatuses = new Map<string, boolean | undefined>();
    const newCameraEnabledStatuses = new Map<string, boolean | undefined>();
    const newScreenShareEnabledStatuses = new Map<string, boolean | undefined>();
    const newRestricted = new Set<string>();
    const newHandRaisedAt = new Map<string, number>();
    allParticipants.forEach((participant) => {
      newMuteStatuses.set(participant.sid, getMuteStatus(participant));
      newCameraStatuses.set(participant.sid, getCameraStatus(participant));
      const enabledState = readEnabledStateFromMetadata(participant);
      newMicEnabledStatuses.set(participant.sid, enabledState.micEnabled);
      newCameraEnabledStatuses.set(participant.sid, enabledState.cameraEnabled);
      newScreenShareEnabledStatuses.set(participant.sid, enabledState.screenShareEnabled);
      if (isRestrictedRole(parseParticipantRole(participant.metadata))) {
        newRestricted.add(participant.sid);
      }
      const raisedAt = parseHandRaisedAt(participant.attributes);
      if (raisedAt !== undefined) newHandRaisedAt.set(participant.sid, raisedAt);
    });
    setMuteStatuses(newMuteStatuses);
    setCameraStatuses(newCameraStatuses);
    setMicEnabledStatuses(newMicEnabledStatuses);
    setCameraEnabledStatuses(newCameraEnabledStatuses);
    setScreenShareEnabledStatuses(newScreenShareEnabledStatuses);
    setRestrictedSids(newRestricted);
    setHandRaisedAt(newHandRaisedAt);
  }, [room, getMuteStatus, getCameraStatus, readEnabledStateFromMetadata]);

  // Option 2: Prevent automatic track stopping when server-muted
  // This keeps the track active so server can unmute it later
  React.useEffect(() => {
    // const handleServerMuted = (publication: any) => {
    //   // If this is a server-side mute (not user-initiated), 
    //   // prevent the track from being stopped
    //   if (publication.source === Track.Source.Microphone && publication.track) {
    //     // Don't stop the track - just let it be muted
    //     // The track should remain active so server can unmute it
    //     console.log('Server muted track - keeping track active for unmute capability');
        
    //     // Prevent automatic track stopping by ensuring track stays enabled
    //     // The track will be muted but not stopped, allowing server to unmute
    //     if (publication.track.mediaStreamTrack) {
    //       // Track is still active, just muted - this is what we want
    //       // Don't call track.stop() or setMicrophoneEnabled(false)
    //     }
    //   }
    // };
    const handleServerMuted = (publication: any) => {
      if (publication.source === Track.Source.Microphone && publication.track) {
        console.log('Server muted track - ensuring track stays active for unmute capability');
        
        // Ensure microphone stays enabled (not stopped) even when muted
        // The track should remain active so server can unmute it
        const micPublication = room.localParticipant.getTrackPublication(Track.Source.Microphone);
        
        if (micPublication && micPublication.track) {
          // Check if track is about to be stopped and prevent it
          // Keep the track enabled but muted

          // if (!room.localParticipant.isMicrophoneEnabled) {
          //   // If microphone was disabled, re-enable it (but it will stay muted)
          //   console.log('Re-enabling microphone to keep track active');
          //   room.localParticipant.setMicrophoneEnabled(true).catch((err) => {
          //     console.warn('Could not re-enable microphone:', err);
          //   });
          // }
        }
      }
    };

    // Listen on local participant for mute events
    room.localParticipant.on('trackMuted', handleServerMuted);
    
    return () => {
      room.localParticipant.off('trackMuted', handleServerMuted);
    };
  }, [room]);

  // Initial load and event listeners
  React.useEffect(() => {
    // Update participants immediately if room is already connected
    if (room.state === 'connected') {
      updateParticipants();
    }

    const handleRoomConnected = () => {
      // When room connects, update participants to include existing remote participants
      updateParticipants();
    };

    const handleParticipantConnected = (participant: RemoteParticipant) => {
      // Update participants list immediately
      updateParticipants();
      
      // Listen for track publications on this specific participant
      const handleParticipantTrackPublished = (publication: any) => {
        if (publication?.source === Track.Source.Microphone || publication?.source === Track.Source.Camera) {
          // Update status when microphone or camera track is published
          updateParticipants();
        }
      };
      
      participant.on('trackPublished', handleParticipantTrackPublished);
      
      // Store cleanup function
      const cleanup = () => {
        participant.off('trackPublished', handleParticipantTrackPublished);
      };
      participantListenersRef.current.set(participant.sid, [cleanup]);
      
      // Also update after a short delay to allow tracks to be published/subscribed
      setTimeout(() => {
        updateParticipants();
      }, 500);
    };

    const handleParticipantDisconnected = (participant: RemoteParticipant) => {
      // Clean up participant-specific listeners
      const listeners = participantListenersRef.current.get(participant.sid);
      if (listeners) {
        listeners.forEach(cleanup => cleanup());
        participantListenersRef.current.delete(participant.sid);
      }
      updateParticipants();
    };

    const handleTrackMuted = () => {
      updateParticipants();
    };

    const handleTrackUnmuted = () => {
      updateParticipants();
    };

    const handleTrackSubscribed = (track: any, publication: any) => {
      // When a track is subscribed, update status for that participant
      if (publication?.source === Track.Source.Microphone || publication?.source === Track.Source.Camera) {
        updateParticipants();
      }
    };

    const handleTrackPublished = (publication: any, participant: RemoteParticipant | LocalParticipant) => {
      // When a track is published, update status
      if (publication?.source === Track.Source.Microphone || publication?.source === Track.Source.Camera) {
        updateParticipants();
      }
    };

    // Handle data messages for PARTICIPANT_ENABLE
    const handleDataReceived = (payload: Uint8Array, participant: any, kind: any, topic: string | undefined) => {
      try {
        const decoder = new TextDecoder();
        const messageText = decoder.decode(payload);
        const message = JSON.parse(messageText);

        if (message.type === 'PARTICIPANT_ENABLE' && message.payload) {
          const { participantSid, trackType, enabled } = message.payload;
          
          if (trackType === 'microphone') {
            setMicEnabledStatuses((prev) => {
              const updated = new Map(prev);
              updated.set(participantSid, enabled);
              return updated;
            });
          } else if (trackType === 'camera') {
            setCameraEnabledStatuses((prev) => {
              const updated = new Map(prev);
              updated.set(participantSid, enabled);
              return updated;
            });
          } else if (trackType === 'screenshare') {
            setScreenShareEnabledStatuses((prev) => {
              const updated = new Map(prev);
              updated.set(participantSid, enabled);
              return updated;
            });
          }
        }
      } catch (error) {
        console.error('Error parsing data message:', error);
      }
    };

    // Handle metadata changes
    const handleParticipantMetadataChanged = (oldMetadata: string | undefined, participant: RemoteParticipant | LocalParticipant) => {
      const enabledState = readEnabledStateFromMetadata(participant);
      setMicEnabledStatuses((prev) => {
        const updated = new Map(prev);
        updated.set(participant.sid, enabledState.micEnabled);
        return updated;
      });
      setCameraEnabledStatuses((prev) => {
        const updated = new Map(prev);
        updated.set(participant.sid, enabledState.cameraEnabled);
        return updated;
      });
      setScreenShareEnabledStatuses((prev) => {
        const updated = new Map(prev);
        updated.set(participant.sid, enabledState.screenShareEnabled);
        return updated;
      });
    };

    // Room-wide policy AND the waiting-room queue: both durable and replayed to
    // late joiners, unlike a packet.
    //
    // Must be synced on Connected as well as on change. Metadata that arrives in
    // the JoinResponse is the room's INITIAL state, not a change, so LiveKit
    // fires no RoomMetadataChanged for it — and this drawer mounts while the
    // room is still connecting, so the call below reads an empty `room.metadata`
    // and finds nothing. That left a moderator who joined a room where someone
    // was ALREADY waiting with a badge on the Participants button (the control
    // bar does sync on Connected) and an empty queue in the drawer behind it.
    // The same gap applied to an already-active room-wide mute.
    const handleRoomMetadataChanged = () => {
      setRoomPolicy(parseRoomPolicy(room.metadata));
      setLobbyEntries(parseLobby(room.metadata));
    };
    handleRoomMetadataChanged();

    // Raised hands. Attributes also arrive in ParticipantInfo, so a moderator
    // joining after a hand went up already sees it via updateParticipants().
    const handleAttributesChanged = (
      _changed: Record<string, string>,
      participant: RemoteParticipant | LocalParticipant,
    ) => {
      const raisedAt = parseHandRaisedAt(participant.attributes);
      setHandRaisedAt((prev) => {
        const updated = new Map(prev);
        if (raisedAt === undefined) updated.delete(participant.sid);
        else updated.set(participant.sid, raisedAt);
        return updated;
      });
    };

    room.on('connected', handleRoomConnected);
    room.on(RoomEvent.Connected, handleRoomMetadataChanged);
    room.on(RoomEvent.ParticipantAttributesChanged, handleAttributesChanged);
    room.on(RoomEvent.RoomMetadataChanged, handleRoomMetadataChanged);
    room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
    room.on(RoomEvent.TrackMuted, handleTrackMuted);
    room.on(RoomEvent.TrackUnmuted, handleTrackUnmuted);
    room.on('trackSubscribed', handleTrackSubscribed);
    room.on('trackPublished', handleTrackPublished);
    room.on(RoomEvent.DataReceived, handleDataReceived);
    room.on(RoomEvent.ParticipantMetadataChanged, handleParticipantMetadataChanged);

    return () => {
      room.off('connected', handleRoomConnected);
      room.off(RoomEvent.Connected, handleRoomMetadataChanged);
      room.off(RoomEvent.ParticipantAttributesChanged, handleAttributesChanged);
      room.off(RoomEvent.RoomMetadataChanged, handleRoomMetadataChanged);
      room.off(RoomEvent.ParticipantConnected, handleParticipantConnected);
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
      room.off(RoomEvent.TrackMuted, handleTrackMuted);
      room.off(RoomEvent.TrackUnmuted, handleTrackUnmuted);
      room.off('trackSubscribed', handleTrackSubscribed);
      room.off('trackPublished', handleTrackPublished);
      room.off(RoomEvent.DataReceived, handleDataReceived);
      room.off(RoomEvent.ParticipantMetadataChanged, handleParticipantMetadataChanged);
      
      // Clean up all participant-specific listeners
      participantListenersRef.current.forEach((listeners) => {
        listeners.forEach(cleanup => cleanup());
      });
      participantListenersRef.current.clear();
    };
  }, [room, updateParticipants, readEnabledStateFromMetadata]);

  // Handle mute/unmute
  const handleToggleMute = React.useCallback(
    async (participant: LocalParticipant | RemoteParticipant) => {
      // Check if mic is enabled before allowing toggle
      const isMicEnabled = micEnabledStatuses.get(participant.sid) ?? true;
      if (!isMicEnabled) {
        console.warn('Cannot toggle mute: microphone is disabled');
        return;
      }

      if (mutingParticipants.has(participant.sid)) {
        return; // Already processing
      }

      const currentMuted = muteStatuses.get(participant.sid) ?? getMuteStatus(participant);
      const newMutedState = !currentMuted;

      setMutingParticipants((prev) => new Set(prev).add(participant.sid));

      try {
        await fetch(
          `/api/participants/mute?roomName=${encodeURIComponent(room.name)}&participantSid=${encodeURIComponent(participant.sid)}&muted=${newMutedState}`
        );
        // Optimistically update UI
        setMuteStatuses((prev) => {
          const updated = new Map(prev);
          updated.set(participant.sid, newMutedState);
          return updated;
        });
      } catch (error) {
        console.error('Error toggling mute:', error);
        // Revert on error - the real status will be updated by event listeners
        updateParticipants();
      } finally {
        setMutingParticipants((prev) => {
          const updated = new Set(prev);
          updated.delete(participant.sid);
          return updated;
        });
      }
    },
    [room.name, muteStatuses, mutingParticipants, getMuteStatus, updateParticipants, micEnabledStatuses]
  );

  // Handle camera enable/disable
  const handleToggleCamera = React.useCallback(
    async (participant: LocalParticipant | RemoteParticipant) => {
      // Check if camera is enabled before allowing toggle
      const isCameraEnabled = cameraEnabledStatuses.get(participant.sid) ?? true;
      if (!isCameraEnabled) {
        console.warn('Cannot toggle camera: camera is disabled');
        return;
      }

      if (togglingCameras.has(participant.sid)) {
        return; // Already processing
      }

      const currentMuted = cameraStatuses.get(participant.sid) ?? getCameraStatus(participant);
      const newMutedState = !currentMuted;

      setTogglingCameras((prev) => new Set(prev).add(participant.sid));

      try {
        await fetch(
          `/api/participants/mute?roomName=${encodeURIComponent(room.name)}&participantSid=${encodeURIComponent(participant.sid)}&muted=${newMutedState}&trackType=camera`
        );
        // Optimistically update UI
        setCameraStatuses((prev) => {
          const updated = new Map(prev);
          updated.set(participant.sid, newMutedState);
          return updated;
        });
      } catch (error) {
        console.error('Error toggling camera:', error);
        // Revert on error - the real status will be updated by event listeners
        updateParticipants();
      } finally {
        setTogglingCameras((prev) => {
          const updated = new Set(prev);
          updated.delete(participant.sid);
          return updated;
        });
      }
    },
    [room.name, cameraStatuses, togglingCameras, getCameraStatus, updateParticipants, cameraEnabledStatuses]
  );

  /**
   * Grant or revoke one device for one person.
   *
   * One handler for all three rather than three near-identical copies: the only
   * things that differ are which state map to read and which room-wide flag (if
   * any) applies, and keeping them in a table means a fourth device — or a fix
   * to the current-state logic — lands in one place.
   */
  const handleToggleDeviceGrant = React.useCallback(
    async (
      participant: LocalParticipant | RemoteParticipant,
      trackType: 'microphone' | 'camera' | 'screenshare',
    ) => {
      // Prevent enabling/disabling own self
      if (participant === room.localParticipant) {
        console.warn(`Cannot toggle ${trackType}: cannot enable/disable own self`);
        return;
      }

      const busySet =
        trackType === 'microphone'
          ? togglingMicEnabled
          : trackType === 'camera'
            ? togglingCameraEnabled
            : togglingScreenShareEnabled;
      if (busySet.has(participant.sid)) return;

      const setBusy =
        trackType === 'microphone'
          ? setTogglingMicEnabled
          : trackType === 'camera'
            ? setTogglingCameraEnabled
            : setTogglingScreenShareEnabled;
      const statuses =
        trackType === 'microphone'
          ? micEnabledStatuses
          : trackType === 'camera'
            ? cameraEnabledStatuses
            : screenShareEnabledStatuses;
      const setStatuses =
        trackType === 'microphone'
          ? setMicEnabledStatuses
          : trackType === 'camera'
            ? setCameraEnabledStatuses
            : setScreenShareEnabledStatuses;
      // Screen share has no room-wide policy of its own.
      const roomWide =
        trackType === 'microphone'
          ? roomPolicy.muteAll
          : trackType === 'camera'
            ? roomPolicy.cameraOffAll
            : undefined;

      setBusy((prev) => new Set(prev).add(participant.sid));

      try {
        // setEnabled, not toggleEnable: the desired state is stated explicitly.
        // Toggling read the old value as `?? true`, which is ambiguous under a
        // room-wide mute or for a restricted participant, where absent means
        // "follow the default", not "enabled". Effective state resolves the
        // override against both, so the first click on someone who is muted or
        // read-only grants them rather than re-muting someone who already looks
        // muted.
        const currentEnabled = !resolveMediaLock({
          individual: statuses.get(participant.sid),
          roomWide,
          isModerator: false,
          restricted: restrictedSids.has(participant.sid),
        }).locked;
        await fetch(
          `/api/participants/mute?action=setEnabled&roomName=${encodeURIComponent(room.name)}&participantSid=${encodeURIComponent(participant.sid)}&trackType=${trackType}&enabled=${!currentEnabled}`
        );
        setStatuses((prev) => {
          const updated = new Map(prev);
          updated.set(participant.sid, !currentEnabled);
          return updated;
        });
      } catch (error) {
        console.error(`Error toggling ${trackType} grant:`, error);
        // Revert on error - the real status will be updated by event listeners
        updateParticipants();
      } finally {
        setBusy((prev) => {
          const updated = new Set(prev);
          updated.delete(participant.sid);
          return updated;
        });
      }
    },
    [
      room,
      micEnabledStatuses,
      cameraEnabledStatuses,
      screenShareEnabledStatuses,
      togglingMicEnabled,
      togglingCameraEnabled,
      togglingScreenShareEnabled,
      restrictedSids,
      updateParticipants,
      roomPolicy,
    ]
  );

  // Flip a room-wide toggle. The route merges the flag into room metadata,
  // sweeps currently-published tracks, and clears stale individual exemptions.
  // We don't optimistically update — RoomMetadataChanged lands on us too.
  const handleToggleRoomPolicy = React.useCallback(
    async (trackType: 'microphone' | 'camera') => {
      if (applyingRoomPolicy.has(trackType)) return;
      const locked = trackType === 'camera' ? !!roomPolicy.cameraOffAll : !!roomPolicy.muteAll;
      setApplyingRoomPolicy((prev) => new Set(prev).add(trackType));
      try {
        const res = await fetch(
          `/api/rooms/media-policy?roomName=${encodeURIComponent(room.name)}&trackType=${trackType}&locked=${!locked}`
        );
        if (!res.ok) {
          console.error('Failed to update room media policy:', res.status, await res.text());
          return;
        }
        const data = await res.json();
        if (data?.failures?.length) {
          console.warn('Room policy applied with per-participant failures:', data.failures);
        }
      } catch (error) {
        console.error('Error updating room media policy:', error);
      } finally {
        setApplyingRoomPolicy((prev) => {
          const updated = new Set(prev);
          updated.delete(trackType);
          return updated;
        });
      }
    },
    [room, roomPolicy, applyingRoomPolicy]
  );

  // Let someone in, or turn them away.
  //
  // No optimistic update: approving only flips the queue entry server-side, and
  // RoomMetadataChanged brings the result back to every moderator at once —
  // which also stops two moderators seeing different queues.
  const handleLobbyDecision = React.useCallback(
    async (entry: LobbyEntry, approve: boolean) => {
      if (decidingTickets.has(entry.id)) return;
      setDecidingTickets((prev) => new Set(prev).add(entry.id));
      try {
        const res = await fetch(
          `/api/lobby/decide?roomName=${encodeURIComponent(room.name)}&ticket=${encodeURIComponent(entry.id)}&approve=${approve}`
        );
        if (!res.ok) {
          console.error('Failed to decide lobby request:', res.status, await res.text());
        }
      } catch (error) {
        console.error('Error deciding lobby request:', error);
      } finally {
        setDecidingTickets((prev) => {
          const updated = new Set(prev);
          updated.delete(entry.id);
          return updated;
        });
      }
    },
    [room, decidingTickets]
  );

  const waitingEntries = React.useMemo(
    () => pendingLobbyEntries(lobbyEntries),
    [lobbyEntries]
  );

  // Dismiss a request without granting it.
  const handleLowerHand = React.useCallback(
    async (participant: LocalParticipant | RemoteParticipant) => {
      if (loweringHands.has(participant.sid)) return;
      setLoweringHands((prev) => new Set(prev).add(participant.sid));
      try {
        const res = await fetch(
          `/api/participants/raise-hand?roomName=${encodeURIComponent(room.name)}&participantSid=${encodeURIComponent(participant.sid)}&raised=false`
        );
        if (!res.ok) {
          console.error('Failed to lower hand:', res.status, await res.text());
        }
        // No optimistic update — ParticipantAttributesChanged is the source of truth.
      } catch (error) {
        console.error('Error lowering hand:', error);
      } finally {
        setLoweringHands((prev) => {
          const updated = new Set(prev);
          updated.delete(participant.sid);
          return updated;
        });
      }
    },
    [room, loweringHands]
  );

  // Raised hands first, oldest request at the top; everyone else keeps their
  // existing order. A queue is only fair if it is worked in the order asked.
  const orderedParticipants = React.useMemo(() => {
    return [...participants].sort((a, b) => {
      const aRaised = handRaisedAt.get(a.sid);
      const bRaised = handRaisedAt.get(b.sid);
      if (aRaised !== undefined && bRaised !== undefined) {
        // Ties broken by identity so the order can't jitter between renders.
        return aRaised - bRaised || a.identity.localeCompare(b.identity);
      }
      if (aRaised !== undefined) return -1;
      if (bRaised !== undefined) return 1;
      return 0;
    });
  }, [participants, handRaisedAt]);

  const getParticipantName = (participant: LocalParticipant | RemoteParticipant): string => {
    return participant.name || participant.identity || 'Unknown';
  };

  // Handle invite modal open/close
  const handleOpenInviteModal = React.useCallback(() => {
    setIsInviteModalOpen(true);
    // Reset form when opening
    setInviteMethod('email');
    setEmail('');
    setPhone('');
    setNickname('');
    setErrors({});
  }, []);

  const handleCloseInviteModal = React.useCallback(() => {
    setIsInviteModalOpen(false);
    // Reset form when closing
    setInviteMethod('email');
    setEmail('');
    setPhone('');
    setNickname('');
    setErrors({});
    setIsSubmitting(false);
  }, []);

  // Form validation
  const validateForm = React.useCallback((): boolean => {
    const newErrors: { email?: string; phone?: string; nickname?: string } = {};

    if (inviteMethod === 'email') {
      if (!email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!email.includes('@')) {
        newErrors.email = 'Email must contain @';
      }
    } else {
      if (!phone.trim()) {
        newErrors.phone = 'Phone is required';
      } else if (!phone.startsWith('+')) {
        newErrors.phone = 'Phone must start with +';
      }
    }

    if (!nickname.trim()) {
      newErrors.nickname = 'Nickname is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [inviteMethod, email, phone, nickname]);

  // Handle form submit
  const handleInviteSubmit = React.useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 1);

      const payload: {
        notificationChannels: { email?: string; phone?: string };
        nickname: string;
        pageId: string | null;
        pending: { invited: boolean; expiresAt: number };
      } = {
        notificationChannels: {},
        nickname: nickname.trim(),
        pageId: searchParams?.get('pageId') || null,
        pending: {
          invited: true,
          expiresAt: expiresAt.getTime(),
        },
      };

      if (inviteMethod === 'email') {
        payload.notificationChannels.email = email.trim();
      } else {
        payload.notificationChannels.phone = phone.trim();
      }

      const response = await fetch('https://hpd-dps-api.staging.beam.live/inviteUser', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to invite user' }));
        throw new Error(errorData.message || 'Failed to invite user');
      }

      // Success - close modal
      handleCloseInviteModal();
    } catch (error) {
      console.error('Error inviting user:', error);
      setErrors({ 
        ...errors, 
        _submit: error instanceof Error ? error.message : 'Failed to invite user' 
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [inviteMethod, email, phone, nickname, searchParams, validateForm, handleCloseInviteModal, errors]);

  return (
    <div className="lk-participants-drawer" {...props}>
      <div className="lk-participants-drawer-content">
        <div className="lk-participants-drawer-header">
          <h2>
            Participants ({participants.length})
            {handRaisedAt.size > 0 && (
              <span style={{ marginLeft: 8, color: '#fbae16', fontSize: '0.875rem' }}>
                ✋ {handRaisedAt.size}
              </span>
            )}
            {canMute && waitingEntries.length > 0 && (
              <span
                title={`${waitingEntries.length} waiting to be let in`}
                style={{ marginLeft: 8, color: '#508afa', fontSize: '0.875rem' }}
              >
                ⏳ {waitingEntries.length}
              </span>
            )}
          </h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {/* Everyone gets this, not just moderators — the person who wants to
                pull a colleague in is usually not the one running the meeting. */}
            <ShareLinkButton roomName={room.name} />
            <button
              onClick={handleOpenInviteModal}
              className="lk-invite-button"
              type="button"
              style={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.5rem 1rem',
                borderRadius: '0.625rem',
                background: 'transparent',
                color: '#40cc52',
                border: '2px solid #40cc52',
                fontSize: '0.875rem',
                fontWeight: 500,
                boxShadow: '0 2px 8px 0 rgba(30, 60, 110, 0.15)',
                outline: 'none',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                // e.currentTarget.style.backgroundColor = '#40cc52';
                // e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                // e.currentTarget.style.backgroundColor = 'trabsparent';
                // e.currentTarget.style.color = '#40cc52';
              }}
            >
              + Invite
            </button>
            <span
              className="lk-participants-close-button"
              role="button"
              tabIndex={0}
              onClick={() => {
                if (onClose) {
                  onClose();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  if (onClose) {
                    onClose();
                  }
                }
              }}
              aria-label="Close"
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'none',
                border: 'none',
                fontSize: 22,
                color: '#000',
                outline: 'none',
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                focusable="false"
              >
                <line x1="5" y1="5" x2="17" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="17" y1="5" x2="5" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
          </div>
        </div>
        {canMute && waitingEntries.length > 0 && (
          <div className="lk-lobby-queue">
            <div className="lk-lobby-queue-title">
              Waiting to join ({waitingEntries.length})
            </div>
            {waitingEntries.map((entry) => {
              const busy = decidingTickets.has(entry.id);
              return (
                <div key={entry.id} className="lk-lobby-queue-item">
                  <div className="lk-lobby-queue-person">
                    <span className="lk-lobby-queue-name">{entry.name}</span>
                    <span className="lk-lobby-queue-time">
                      asked at {new Date(entry.requestedAt).toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="lk-lobby-queue-actions">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => handleLobbyDecision(entry, true)}
                      className="lk-lobby-admit-button"
                      title={`Let ${entry.name} into the meeting`}
                    >
                      {busy ? '…' : 'Admit'}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => handleLobbyDecision(entry, false)}
                      className="lk-lobby-deny-button"
                      title={`Turn ${entry.name} away`}
                    >
                      Deny
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {canMute && (
          <div
            className="lk-room-policy-bar"
            style={{
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'center',
              padding: '0.5rem 1rem 0.75rem',
              borderBottom: '1px solid #d0daea',
            }}
          >
            {([
              {
                trackType: 'microphone' as const,
                locked: !!roomPolicy.muteAll,
                onLabel: 'Unmute all',
                offLabel: 'Mute all',
              },
              {
                trackType: 'camera' as const,
                locked: !!roomPolicy.cameraOffAll,
                onLabel: 'Unmute cameras',
                offLabel: 'Mute cameras',
              },
            ]).map(({ trackType, locked, onLabel, offLabel }) => {
              const busy = applyingRoomPolicy.has(trackType);
              return (
                <button
                  key={trackType}
                  type="button"
                  disabled={busy}
                  onClick={() => handleToggleRoomPolicy(trackType)}
                  title={
                    locked
                      ? 'Lift this for everyone. Individually muted people stay muted.'
                      : 'Applies to everyone now and to anyone who joins later.'
                  }
                  style={{
                    cursor: busy ? 'wait' : 'pointer',
                    padding: '0.375rem 0.75rem',
                    borderRadius: '0.625rem',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    // Amber, not red, when active: this is a state indicator
                    // ("a room-wide mute is in effect") and red on a button
                    // labelled "Unmute all" reads as though unmuting is the
                    // destructive act.
                    background: locked ? '#fbae16' : 'transparent',
                    color: locked ? '#0c2340' : '#0c2340',
                    border: `2px solid ${locked ? '#fbae16' : '#d0daea'}`,
                    opacity: busy ? 0.6 : 1,
                    outline: 'none',
                  }}
                >
                  {locked ? onLabel : offLabel}
                </button>
              );
            })}
          </div>
        )}
        <div className="lk-participants-list">
          {orderedParticipants.map((participant) => {
            const isMuted = muteStatuses.get(participant.sid) ?? getMuteStatus(participant);
            const isCameraMuted = cameraStatuses.get(participant.sid) ?? getCameraStatus(participant);
            const isLocal = participant === room.localParticipant;
            const isMuting = mutingParticipants.has(participant.sid);
            const isTogglingCamera = togglingCameras.has(participant.sid);
            const isTogglingMicEnabled = togglingMicEnabled.has(participant.sid);
            const isTogglingCameraEnabled = togglingCameraEnabled.has(participant.sid);
            const isTogglingScreenShareEnabled = togglingScreenShareEnabled.has(participant.sid);
            const isRestricted = restrictedSids.has(participant.sid);
            // Effective state, not the raw override — someone with no override
            // of their own still shows as disabled while a room-wide mute is on,
            // or while they're read-only / here on a shared link.
            const micOverride = micEnabledStatuses.get(participant.sid);
            const cameraOverride = cameraEnabledStatuses.get(participant.sid);
            const screenShareOverride = screenShareEnabledStatuses.get(participant.sid);
            const isMicEnabled = !resolveMediaLock({
              individual: micOverride,
              roomWide: roomPolicy.muteAll,
              isModerator: false,
              restricted: isRestricted,
            }).locked;
            const isCameraEnabled = !resolveMediaLock({
              individual: cameraOverride,
              roomWide: roomPolicy.cameraOffAll,
              isModerator: false,
              restricted: isRestricted,
            }).locked;
            const isScreenShareEnabled = !resolveMediaLock({
              individual: screenShareOverride,
              roomWide: undefined,
              isModerator: false,
              restricted: isRestricted,
            }).locked;
            // An explicit `true` under a room-wide mute is a moderator exemption
            // — surfaced so they can tell "allowed" from "just not covered yet".
            const isExempt =
              (roomPolicy.muteAll && micOverride === true) ||
              (roomPolicy.cameraOffAll && cameraOverride === true);
            const raisedAt = handRaisedAt.get(participant.sid);
            const isHandRaised = raisedAt !== undefined;
            const isLoweringHand = loweringHands.has(participant.sid);

            return (
              <div key={participant.sid} className="lk-participant-item">
                <div className="lk-participant-info">
                  <span className="lk-participant-name" style={{ color: '#000' }}>
                    {getParticipantName(participant)}
                    {isLocal && <span className="lk-participant-badge">(You)</span>}
                    {isRestricted && (
                      <span
                        className="lk-participant-badge"
                        title="Read-only access, or joined from a shared link. Use the buttons to let them speak or share."
                        style={{ color: '#508afa' }}
                      >
                        (view only)
                      </span>
                    )}
                    {isExempt && (
                      <span
                        className="lk-participant-badge"
                        title="Allowed by a moderator despite the room-wide mute"
                        style={{ color: '#40cc52' }}
                      >
                        (allowed)
                      </span>
                    )}
                  </span>
                  {isHandRaised && (
                    <div className="lk-hand-raised-row">
                      <span
                        className="lk-participant-badge"
                        title={`Asked to speak at ${new Date(raisedAt).toLocaleTimeString()}`}
                        style={{ color: '#fbae16' }}
                      >
                        ✋ raised
                      </span>
                      {canMute && !isLocal && (
                        <button
                          type="button"
                          disabled={isLoweringHand}
                          onClick={() => handleLowerHand(participant)}
                          title="Dismiss this request without allowing them to talk"
                          className="lk-dismiss-hand-button"
                          style={{ opacity: isLoweringHand ? 0.6 : 1 }}
                        >
                          Dismiss
                        </button>
                      )}
                    </div>
                  )}
                  <div className="lk-participant-status">
                    <span className={`lk-mute-indicator ${isMuted ? 'muted' : 'unmuted'}`} style={{ color: '#000' }}>
                      {isMuted ? <MicDisabledIcon /> : <MicIcon />}
                    </span>
                    <span className="lk-mute-status-text">
                      {isMuted ? 'Muted' : 'Unmuted'}
                    </span>
                    <span className={`lk-camera-indicator ${isCameraMuted ? 'muted' : 'unmuted'}`} style={{ color: '#000', marginLeft: 8 }}>
                      {isCameraMuted ? <CameraDisabledIcon /> : <CameraIcon />}
                    </span>
                    <span className="lk-mute-status-text" style={{ marginLeft: 4 }}>
                      {isCameraMuted ? 'Camera Off' : 'Camera On'}
                    </span>
                  </div>
                </div>
                {canMute && (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                    {/* Raised-hand actions. Granting the mic also lowers the
                        hand server-side, in the same write as the grant. */}
                    {/* Microphone Enable/Disable Button */}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => !isTogglingMicEnabled && !isLocal && handleToggleDeviceGrant(participant, 'microphone')}
                      onKeyDown={e => {
                        if ((e.key === 'Enter' || e.key === ' ') && !isTogglingMicEnabled && !isLocal) handleToggleDeviceGrant(participant, 'microphone');
                      }}
                      className={`lk-mic-enable-toggle-icon${isTogglingMicEnabled ? ' toggling' : ''}`}
                      title={isLocal ? 'Cannot enable/disable own mic' : (isMicEnabled ? 'Disable Mic' : 'Enable Mic')}
                      aria-disabled={isTogglingMicEnabled || isLocal}
                      style={{
                        cursor: (isTogglingMicEnabled || isLocal) ? 'not-allowed' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '25%',
                        boxShadow: '0 2px 8px 0 rgba(30, 60, 110, 0.15), 0 0px 0px #fff',
                        background: isMicEnabled ? '#eff2f7' : '#f0f0f0',
                        color: isMicEnabled ? '#000' : '#999',
                        outline: 'none',
                        width: 36,
                        height: 36,
                        opacity: (isTogglingMicEnabled || isLocal) ? 0.6 : (isMicEnabled ? 1 : 0.6),
                        fontSize: 18
                      }}
                    >
                      {isTogglingMicEnabled
                        ? <span style={{ fontSize: 22 }}>…</span>
                        : (isMicEnabled ? <MicIcon /> : <MicDisabledIcon />)
                      }
                    </span>
                    {/* Camera Enable/Disable Button */}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => !isTogglingCameraEnabled && !isLocal && handleToggleDeviceGrant(participant, 'camera')}
                      onKeyDown={e => {
                        if ((e.key === 'Enter' || e.key === ' ') && !isTogglingCameraEnabled && !isLocal) handleToggleDeviceGrant(participant, 'camera');
                      }}
                      className={`lk-camera-enable-toggle-icon${isTogglingCameraEnabled ? ' toggling' : ''}`}
                      title={isLocal ? 'Cannot enable/disable own camera' : (isCameraEnabled ? 'Disable Camera' : 'Enable Camera')}
                      aria-disabled={isTogglingCameraEnabled || isLocal}
                      style={{
                        cursor: (isTogglingCameraEnabled || isLocal) ? 'not-allowed' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '25%',
                        boxShadow: '0 2px 8px 0 rgba(30, 60, 110, 0.15), 0 0px 0px #fff',
                        background: isCameraEnabled ? '#eff2f7' : '#f0f0f0',
                        color: isCameraEnabled ? '#000' : '#999',
                        outline: 'none',
                        width: 36,
                        height: 36,
                        opacity: (isTogglingCameraEnabled || isLocal) ? 0.6 : (isCameraEnabled ? 1 : 0.6),
                        fontSize: 18
                      }}
                    >
                      {isTogglingCameraEnabled
                        ? <span style={{ fontSize: 22 }}>…</span>
                        : (isCameraEnabled ? <CameraIcon /> : <CameraDisabledIcon />)
                      }
                    </span>
                    {/* Screen Share Enable/Disable Button */}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => !isTogglingScreenShareEnabled && !isLocal && handleToggleDeviceGrant(participant, 'screenshare')}
                      onKeyDown={e => {
                        if ((e.key === 'Enter' || e.key === ' ') && !isTogglingScreenShareEnabled && !isLocal) handleToggleDeviceGrant(participant, 'screenshare');
                      }}
                      className={`lk-screenshare-enable-toggle-icon${isTogglingScreenShareEnabled ? ' toggling' : ''}`}
                      title={isLocal ? 'Cannot enable/disable own screen share' : (isScreenShareEnabled ? 'Disable Screen Share' : 'Enable Screen Share')}
                      aria-disabled={isTogglingScreenShareEnabled || isLocal}
                      style={{
                        cursor: (isTogglingScreenShareEnabled || isLocal) ? 'not-allowed' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '25%',
                        boxShadow: '0 2px 8px 0 rgba(30, 60, 110, 0.15), 0 0px 0px #fff',
                        background: isScreenShareEnabled ? '#eff2f7' : '#f0f0f0',
                        color: isScreenShareEnabled ? '#000' : '#999',
                        outline: 'none',
                        width: 36,
                        height: 36,
                        opacity: (isTogglingScreenShareEnabled || isLocal) ? 0.6 : (isScreenShareEnabled ? 1 : 0.6),
                        fontSize: 18
                      }}
                    >
                      {isTogglingScreenShareEnabled
                        ? <span style={{ fontSize: 22 }}>…</span>
                        : (
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <path
                              fill="currentColor"
                              d="M1.5 2h13a.5.5 0 0 1 .5.5v9a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5ZM2 3v8h12V3H2Zm3.5 10h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1 0-1Z"
                            />
                            {!isScreenShareEnabled && (
                              <path
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                d="M2 14 14 2"
                              />
                            )}
                          </svg>
                        )
                      }
                    </span>
                    {/* Microphone Mute/Unmute Button (disabled when mic is disabled) */}
                    {/* <span
                      role="button"
                      tabIndex={0}
                      onClick={() => !isMuting && isMicEnabled && handleToggleMute(participant)}
                      onKeyDown={e => {
                        if ((e.key === 'Enter' || e.key === ' ') && !isMuting && isMicEnabled) handleToggleMute(participant);
                      }}
                      className={`lk-mute-toggle-icon${isMuting ? ' muting' : ''}`}
                      title={isMuted ? 'Unmute' : 'Mute'}
                      aria-disabled={isMuting || !isMicEnabled}
                      style={{
                        cursor: (isMuting || !isMicEnabled) ? 'not-allowed' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '25%',
                        boxShadow: '0 2px 8px 0 rgba(30, 60, 110, 0.15), 0 0px 0px #fff',
                        background: '#eff2f7',
                        color: '#000',
                        outline: 'none',
                        width: 36,
                        height: 36,
                        opacity: (isMuting || !isMicEnabled) ? 0.6 : 1,
                        fontSize: 18
                      }}
                    >
                      {isMuting
                        ? <span style={{ fontSize: 22 }}>…</span>
                        : (isMuted ? <MicIcon /> : <MicDisabledIcon />)
                      }
                    </span> */}
                    {/* Camera Mute/Unmute Button (disabled when camera is disabled) */}
                    {/* <span
                      role="button"
                      tabIndex={0}
                      onClick={() => !isTogglingCamera && isCameraEnabled && handleToggleCamera(participant)}
                      onKeyDown={e => {
                        if ((e.key === 'Enter' || e.key === ' ') && !isTogglingCamera && isCameraEnabled) handleToggleCamera(participant);
                      }}
                      className={`lk-camera-toggle-icon${isTogglingCamera ? ' toggling' : ''}`}
                      title={isCameraMuted ? 'Enable Camera' : 'Disable Camera'}
                      aria-disabled={isTogglingCamera || !isCameraEnabled}
                      style={{
                        cursor: (isTogglingCamera || !isCameraEnabled) ? 'not-allowed' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '25%',
                        boxShadow: '0 2px 8px 0 rgba(30, 60, 110, 0.15), 0 0px 0px #fff',
                        background: '#eff2f7',
                        color: '#000',
                        outline: 'none',
                        width: 36,
                        height: 36,
                        opacity: (isTogglingCamera || !isCameraEnabled) ? 0.6 : 1,
                        fontSize: 18
                      }}
                    >
                      {isTogglingCamera
                        ? <span style={{ fontSize: 22 }}>…</span>
                        : (isCameraMuted ? <CameraIcon /> : <CameraDisabledIcon />)
                      }
                    </span> */}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div
        className="lk-participants-drawer-overlay"
        onClick={() => {
          if (onClose) {
            onClose();
          }
        }}
      />
      {/* Invite Modal */}
      {isInviteModalOpen && (
        <div className="lk-invite-modal">
          <div className="lk-invite-modal-overlay" onClick={handleCloseInviteModal} />
          <div className="lk-invite-modal-content">
            <div className="lk-invite-modal-header">
              <h2>Add Participant</h2>
              <span
                className="lk-invite-modal-close-button"
                role="button"
                tabIndex={0}
                onClick={handleCloseInviteModal}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleCloseInviteModal();
                  }
                }}
                aria-label="Close"
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  focusable="false"
                >
                  <line x1="5" y1="5" x2="17" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <line x1="17" y1="5" x2="5" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
            </div>
            <form onSubmit={handleInviteSubmit} className="lk-invite-modal-form">
              <div className="lk-invite-modal-body">
                {/* Invite Method Radio Buttons */}
                <div className="lk-invite-method-group">
                  {/* <label className="lk-invite-method-label">Invite via</label> */}
                  <div className="lk-toggle-button-group">
                    <button
                      type="button"
                      onClick={() => {
                        setInviteMethod('email');
                        setErrors({ ...errors, email: undefined });
                      }}
                      className={`lk-toggle-button ${inviteMethod === 'email' ? 'lk-toggle-button-active' : ''}`}
                    >
                      Email
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setInviteMethod('phone');
                        setErrors({ ...errors, phone: undefined });
                      }}
                      className={`lk-toggle-button ${inviteMethod === 'phone' ? 'lk-toggle-button-active' : ''}`}
                    >
                      Phone
                    </button>
                  </div>
                </div>

                {/* Email Input */}
                {inviteMethod === 'email' && (
                  <div className="lk-form-field">
                    <label htmlFor="email" className="lk-form-label">
                      Email <span style={{ color: '#fa3343' }}>*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errors.email) {
                          setErrors({ ...errors, email: undefined });
                        }
                      }}
                      className={`lk-form-input ${errors.email ? 'lk-form-input-error' : ''}`}
                      placeholder="example@email.com"
                      disabled={isSubmitting}
                    />
                    {errors.email && (
                      <span className="lk-form-error">{errors.email}</span>
                    )}
                  </div>
                )}

                {/* Phone Input */}
                {inviteMethod === 'phone' && (
                  <div className="lk-form-field">
                    <label htmlFor="phone" className="lk-form-label">
                      Phone <span style={{ color: '#fa3343' }}>*</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value);
                        if (errors.phone) {
                          setErrors({ ...errors, phone: undefined });
                        }
                      }}
                      className={`lk-form-input ${errors.phone ? 'lk-form-input-error' : ''}`}
                      placeholder="+1234567890"
                      disabled={isSubmitting}
                    />
                    {errors.phone && (
                      <span className="lk-form-error">{errors.phone}</span>
                    )}
                  </div>
                )}

                {/* Nickname Input */}
                <div className="lk-form-field">
                  <label htmlFor="nickname" className="lk-form-label">
                    Nickname <span style={{ color: '#fa3343' }}>*</span>
                  </label>
                  <input
                    id="nickname"
                    type="text"
                    value={nickname}
                    onChange={(e) => {
                      setNickname(e.target.value);
                      if (errors.nickname) {
                        setErrors({ ...errors, nickname: undefined });
                      }
                    }}
                    className={`lk-form-input ${errors.nickname ? 'lk-form-input-error' : ''}`}
                    placeholder="Enter nickname"
                    disabled={isSubmitting}
                  />
                  {errors.nickname && (
                    <span className="lk-form-error">{errors.nickname}</span>
                  )}
                </div>

                {/* Submit Error */}
                {errors._submit && (
                  <div className="lk-form-error" style={{ marginTop: '0.5rem' }}>
                    {errors._submit}
                  </div>
                )}
              </div>
              <div className="lk-invite-modal-footer">
                <button
                  type="button"
                  onClick={handleCloseInviteModal}
                  className="lk-invite-modal-cancel-button"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="lk-invite-modal-submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Inviting...' : 'Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

